import { MAX_STRIKE_PRICE_DECIMAL_PLACES } from '@src/components/OMS/ManualDealTradeView/utils';
import type { OptionDealProductBuilderRow, OTCOptionFieldsData } from '@src/types/OTC/otcProductBuilderTypes';
import {
  getPriceSourceFixingsByFullSymbolFromMapping,
  USD_PEGGED_NO_CONVERSION_INDEX,
  type FixingIndexMappingMap,
} from '@src/utils/fixtures/OTC/fixingFeedUtils';
import { OPTION_AVAILABLE_UNDERLYING_SYMBOLS } from '@src/utils/OTC/otcUtils';
import {
  EMPTY_OBJECT,
  ExerciseStyleEnum,
  OptionTypeEnum,
  OTCSettlementTypeEnum,
  PayoffTypeEnum,
  runValidationV1,
  StrikeTypeEnum,
} from '@talos/kyoko';
import type { WritableDraft } from 'immer';
import { object, string, type InferType } from 'yup-v1-latest';
import { applyOptionOtcFieldsUpdate } from './applyOptionOtcFieldsUpdate';
import type { BaseProductBuilderSliceStrategy } from './BaseProductBuilderSliceStrategy';
import { positiveNumericString } from './validationUtils';

export const OptionProductBuilderSliceStrategy: BaseProductBuilderSliceStrategy<OptionDealProductBuilderRow> = {
  getDefaultValues: (
    existingOTCFieldsForClear?: OptionDealProductBuilderRow['otcFields']
  ): NonNullable<OptionDealProductBuilderRow['otcFields']> => {
    const optionDefaults: OTCOptionFieldsData = {
      productType: 'option',
      payoffType: undefined,
      quoteAsset: '',
      deliveryAsset: undefined,
      unitNotionalAmount: undefined,
      tenor: undefined,
      strikeType: 'Fixed',
      strikePrice: undefined,
      exerciseStyle: 'European',
      settlementType: OTCSettlementTypeEnum.Cash,
      paymentCcy: undefined, // Computed based on payoff type
      expirationDate: undefined,
      index: '',
    };

    return { ...optionDefaults, ...existingOTCFieldsForClear };
  },

  buildNoSuffixInstrumentName: (updatedRow: WritableDraft<OptionDealProductBuilderRow>): string => {
    if (!updatedRow || !updatedRow.baseAsset || !updatedRow.optionType || !updatedRow.otcFields?.quoteAsset) {
      return '';
    }

    const { baseAsset, optionType } = updatedRow;
    const { quoteAsset, tenor, strikeType, strikePrice, index } = updatedRow.otcFields;

    // Format: OPT-{Underlying}-{Quote}[-{Tenor}][-{Index} when Delta]-{Strike}-{Call/Put}-{E}
    let instrument = `OPT-${baseAsset}-${quoteAsset}`;

    // Add tenor if available
    if (tenor) {
      instrument += `-${tenor}`;
    }

    // Add strike based on strike type
    if (strikeType === StrikeTypeEnum.Delta && strikePrice) {
      const indexName = index?.trim() ?? '';
      if (indexName) {
        instrument += `-${indexName}`;
      }
      instrument += `-${strikePrice}D`;
    } else if (strikeType === StrikeTypeEnum.Fixed && strikePrice) {
      // Fixed Strike: e.g., 80000
      instrument += `-${strikePrice}`;
    }
    // If Fixed Strike not set, skip strike part

    // Add option type (C for Call, P for Put)
    const optionTypeAbbr = optionType === OptionTypeEnum.Call ? 'C' : 'P';
    instrument += `-${optionTypeAbbr}`;

    // Always add E for European
    instrument += '-E';

    return instrument;
  },

  updateGridRowData: (
    state: WritableDraft<OptionDealProductBuilderRow>,
    rowData: OptionDealProductBuilderRow,
    fixingIndexMapping?: FixingIndexMappingMap
  ): void => {
    const oldBaseAsset = state.baseAsset;
    state.baseAsset = rowData.baseAsset;
    state.optionType = rowData.optionType;
    state.productTemplate = rowData.productTemplate;
    state.uniqueSuffix = rowData.uniqueSuffix;

    const baseAssetChanged = oldBaseAsset !== rowData.baseAsset;

    // When underlying changes for Inverse payoff, quote and delivery track underlying (premium-in-base convention)
    if (state.otcFields?.payoffType === 'Inverse' && baseAssetChanged) {
      state.otcFields.quoteAsset = rowData.baseAsset || '';
      state.otcFields.deliveryAsset = rowData.baseAsset || undefined;
    }

    // When baseAsset changes, update index if current index is invalid for new baseAsset-quoteAsset pair
    if (baseAssetChanged && state.baseAsset && state.otcFields && fixingIndexMapping) {
      const quoteAssetForIndexLookup =
        state.otcFields.payoffType === 'Inverse'
          ? 'USD'
          : state.otcFields.quoteAsset === 'USDT' || state.otcFields.quoteAsset === 'USDC'
            ? 'USD'
            : state.otcFields.quoteAsset;

      if (quoteAssetForIndexLookup) {
        const fixings = getPriceSourceFixingsByFullSymbolFromMapping(
          state.baseAsset,
          quoteAssetForIndexLookup,
          fixingIndexMapping
        );
        const availableIndexes = fixings
          .filter(fixing => fixing.Name !== USD_PEGGED_NO_CONVERSION_INDEX.Name)
          .map(fixing => fixing.Name);

        if (availableIndexes.length > 0) {
          const currentIndex = state.otcFields.index;
          const currentIndexIsValid = currentIndex && availableIndexes.includes(currentIndex);

          // If current index is invalid or missing, select the first available (or single if only one)
          if (!currentIndexIsValid) {
            state.otcFields.index = availableIndexes[0];
          }
        } else {
          // No indexes available, clear it (validation will catch this)
          state.otcFields.index = '';
        }
      }
    }
  },

  updateOTCFields: (
    state: WritableDraft<OptionDealProductBuilderRow>,
    otcFields: OptionDealProductBuilderRow['otcFields'],
    fixingIndexMapping?: FixingIndexMappingMap
  ): void => {
    applyOptionOtcFieldsUpdate(state, otcFields, fixingIndexMapping);
  },

  validateFormState: (
    currentRow: OptionDealProductBuilderRow,
    fixingIndexMapping?: FixingIndexMappingMap
  ): ReturnType<typeof runValidationV1> => {
    const schema = fixingIndexMapping
      ? createOptionProductBuilderSchema(fixingIndexMapping)
      : optionProductBuilderSchema;
    return runValidationV1(schema, currentRow, { multipleErrorMode: 'inline' });
  },

  validateOTCFields: (
    otcFields: OptionDealProductBuilderRow['otcFields'],
    fixingIndexMapping?: FixingIndexMappingMap
  ): ReturnType<typeof runValidationV1> => {
    return runValidationV1(optionOtcFieldsSchema, otcFields ?? EMPTY_OBJECT, { multipleErrorMode: 'inline' });
  },
};

/** UI-side validation schema for option OTC Fields form */
export const optionOtcFieldsSchema = object().shape({
  productType: string()
    .oneOf(['option'] as const)
    .required('Product type is required'),
  payoffType: string()
    .oneOf([PayoffTypeEnum.Linear, PayoffTypeEnum.Inverse, PayoffTypeEnum.Quanto] as const)
    .required('Payoff type is required'),
  quoteAsset: string().required('Quote asset is required'),
  // deliveryAsset is optional for all payoff types:
  // - For Inverse: it's derived from baseAsset
  // - For Linear and Quanto: quoteAsset serves as default when deliveryAsset is not set
  deliveryAsset: string().optional(),
  unitNotionalAmount: positiveNumericString('Unit notional amount'),
  strikeType: string()
    .oneOf([StrikeTypeEnum.Fixed, StrikeTypeEnum.Delta] as const)
    .required('Strike type is required'),
  strikePrice: string().when('strikeType', {
    is: StrikeTypeEnum.Fixed,
    then: schema =>
      schema.optional().test('strike-price-validation', 'Invalid strike price', function (value) {
        // If value is provided, validate it
        if (!value || value === '') {
          return true; // Optional, so empty is valid
        }

        const num = Number(value);
        if (isNaN(num)) {
          return this.createError({ message: 'Strike price must be a valid number' });
        }
        if (num <= 0) {
          return this.createError({ message: 'Strike price must be greater than 0 for Fixed strike type' });
        }
        // Max decimal places
        const parts = value.split('.');
        if (parts.length > 1 && parts[1].length > MAX_STRIKE_PRICE_DECIMAL_PLACES) {
          return this.createError({
            message: `Strike price can have at most ${MAX_STRIKE_PRICE_DECIMAL_PLACES} decimal places`,
          });
        }
        return true;
      }),
    otherwise: schema =>
      schema.optional().test('strike-price-validation', 'Invalid strike price', function (value) {
        // If value is blank, valid (optional — consistent with Fixed)
        if (!value || value === '') {
          return true;
        }

        // Delta Strike: must be between 0 and 100 (exclusive) when provided
        const num = Number(value);
        if (isNaN(num) || num <= 0 || num >= 100) {
          return this.createError({
            message: 'Strike price must be between 0 and 100 (exclusive) for Delta strike type',
          });
        }
        return true;
      }),
  }),
  exerciseStyle: string()
    .oneOf([ExerciseStyleEnum.European] as const)
    .required('Exercise style is required')
    .default(ExerciseStyleEnum.European),
  settlementType: string()
    .oneOf([OTCSettlementTypeEnum.Cash, OTCSettlementTypeEnum.Physical] as const)
    .required('Settlement type is required'),
  paymentCcy: string().optional(), // Computed field, not user input
  tenor: string().optional(),
  expirationDate: string().optional(), // Can be set in Trade Booking
  index: string().required('Index is required'),
});

export const createOptionProductBuilderSchema = (fixingIndexMapping?: FixingIndexMappingMap) =>
  object({
    productTemplate: string().required('Product Template is required'),
    product: string()
      .oneOf(['option'] as const)
      .required('Product is required'),
    baseAsset: string()
      .required('Underlying is required')
      .oneOf([...OPTION_AVAILABLE_UNDERLYING_SYMBOLS], 'Invalid underlying symbol'),
    optionType: string()
      .oneOf([OptionTypeEnum.Call, OptionTypeEnum.Put] as const)
      .required('Option type is required'),
    uniqueSuffix: string().nullable(),
    otcFields: optionOtcFieldsSchema
      .required('OTC fields are required')
      .test(
        'index-valid-for-pair',
        'Index must be valid for the selected Underlying and Quote Asset pair',
        function (otcFields) {
          if (!otcFields || !this.parent.baseAsset || !otcFields.quoteAsset || !otcFields.index) {
            return true;
          }

          const normalizedQuoteAsset =
            otcFields.payoffType === PayoffTypeEnum.Inverse
              ? 'USD'
              : otcFields.quoteAsset === 'USDT' || otcFields.quoteAsset === 'USDC'
                ? 'USD'
                : otcFields.quoteAsset;

          if (fixingIndexMapping && fixingIndexMapping.size > 0) {
            const fixings = getPriceSourceFixingsByFullSymbolFromMapping(
              this.parent.baseAsset,
              normalizedQuoteAsset,
              fixingIndexMapping
            );
            const availableIndexes = fixings
              .filter(fixing => fixing.Name !== USD_PEGGED_NO_CONVERSION_INDEX.Name)
              .map(fixing => fixing.Name);
            return availableIndexes.includes(otcFields.index);
          }

          return true;
        }
      )
      .test(
        'inverse-quote-delivery-match-base',
        'For Inverse options, quote and delivery must match underlying',
        function (otcFields) {
          if (!otcFields || otcFields.payoffType !== PayoffTypeEnum.Inverse) {
            return true;
          }
          const base = this.parent.baseAsset;
          // Defensive: OTC Fields is disabled until underlying is set; skip so base-asset required errors are not masked by this rule.
          if (!base) {
            return true;
          }
          return otcFields.quoteAsset === base && otcFields.deliveryAsset === base;
        }
      ),
  });

export const optionProductBuilderSchema = createOptionProductBuilderSchema();

export type ValidatedOptionRowData = InferType<typeof optionProductBuilderSchema>;
